/**
 * Unified share helper. Picks the best available method in this order:
 *
 *   1. Native iOS/Android system share sheet (via @capacitor/share) — only
 *      when running inside the Capacitor wrapper. Opens iMessage / WhatsApp /
 *      Mail / Instagram-DM / etc. as one-tap targets.
 *   2. Web Share API (navigator.share) — supported on iOS Safari, Android
 *      Chrome, and most modern mobile browsers. Same look as native share.
 *   3. Clipboard fallback (navigator.clipboard.writeText) — works everywhere
 *      else (desktop Chrome/Firefox without Share API, etc.).
 *
 * Returns `{ method, ok }` so callers can show a contextual toast.
 * `method` is one of: 'native' | 'web' | 'clipboard' | 'cancelled'
 */
import { Share } from '@capacitor/share';

import { isNative } from '@/native/permissions';

export async function shareLink({ title, text, url, dialogTitle }) {
  // 1) Native (iOS / Android via Capacitor)
  if (isNative()) {
    try {
      const can = await Share.canShare();
      if (can?.value !== false) {
        await Share.share({
          title: title || 'TheUnityBlaze',
          text: text || '',
          url,
          dialogTitle: dialogTitle || 'Share',
        });
        return { method: 'native', ok: true };
      }
    } catch (e) {
      // User dismissed the share sheet — surface as cancelled, no toast.
      const msg = String(e?.message || e);
      if (/cancel|abort|dismiss/i.test(msg)) return { method: 'cancelled', ok: false };
      // Fall through to web/clipboard on real errors.
    }
  }

  // 2) Web Share API (mobile browsers + macOS Safari)
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: title || 'TheUnityBlaze', text: text || '', url });
      return { method: 'web', ok: true };
    } catch (e) {
      if (e?.name === 'AbortError') return { method: 'cancelled', ok: false };
      // Fall through.
    }
  }

  // 3) Clipboard fallback
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(url);
      return { method: 'clipboard', ok: true };
    } catch (e) {
      // last-ditch failure
    }
  }

  return { method: 'cancelled', ok: false };
}
